import { useSearchParams } from "react-router";
import { deactivateTagAction, restoreTagAction } from "@/actions/tags";
import { useApiQuery } from "@/api/useApiQuery";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { LinkButton } from "@/components/LinkButton";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { StatusBadge } from "@/components/StatusBadge";
import type { TagListRow } from "@shared/dto";
import { formatUsage } from "@shared/usage";
import { TagEditRow } from "./tags/TagEditRow";
import { TagMergeRow } from "./tags/TagMergeRow";

const columns: DataTableColumn<TagListRow>[] = [
  {
    key: "slug",
    label: "Slug",
    info: "Kebab-case vocabulary entry. Tags are a suggestion list, not a constraint — templates store them as free text.",
    render: (row) => row.slug,
  },
  { key: "label", label: "Label", render: (row) => row.label },
  {
    key: "kind",
    label: "Kind",
    info: "Marks which tags are promotion candidates: EQUIPMENT, COST and SEASON are meant to become columns later. FREE is everything else.",
    render: (row) => row.kind,
  },
  {
    key: "templateCount",
    label: "Used by",
    info: "Quest templates carrying this tag. Counted by containment, since templates hold tags as plain strings rather than through a foreign key.",
    render: (row) => row.templateCount,
  },
  {
    key: "isActive",
    label: "State",
    render: (row) => (
      <StatusBadge
        label={row.isActive ? "Active" : "Inactive"}
        tone={row.isActive ? "accent" : "neutral"}
      />
    ),
  },
];

export const TagsPage = () => {
  const [searchParams] = useSearchParams();
  const { data, error, isLoading, refresh } =
    useApiQuery<TagListRow[]>("/tags");

  const tags = data ?? [];
  const mergingKey = searchParams.get("merge") ?? undefined;
  // Merging deletes a row, so a half-open merge outranks an edit link rather
  // than letting both render over the same row.
  const editingKey =
    mergingKey !== undefined
      ? undefined
      : (searchParams.get("edit") ?? undefined);

  return (
    <>
      <PageHeader kicker="Taxonomy" title="Tags" />
      <DataTable
        label="Tags"
        columns={columns}
        rows={tags}
        getRowKey={(row) => row.slug}
        emptyMessage={error ?? (isLoading ? "Loading..." : "No tags yet.")}
        editingKey={editingKey ?? mergingKey}
        renderEditRow={(row) =>
          row.slug === mergingKey ? (
            <TagMergeRow
              slug={row.slug}
              templateCount={row.templateCount}
              candidates={tags
                .filter((candidate) => candidate.slug !== row.slug)
                .map((candidate) => ({
                  slug: candidate.slug,
                  label: candidate.label,
                }))}
              onSaved={refresh}
            />
          ) : (
            <TagEditRow
              slug={row.slug}
              label={row.label}
              kind={row.kind}
              onSaved={refresh}
            />
          )
        }
        rowActions={(row) => (
          <RowActions
            editHref={`/tags?edit=${row.slug}`}
            isInactive={!row.isActive}
            retireLabel="Deactivate"
            retirePendingLabel="Deactivating..."
            confirmTitle={`Deactivate ${row.slug}?`}
            confirmBody={
              <>
                <p>{formatUsage(row.templateCount, "quest template")}</p>
                <p>
                  Templates hold tags as plain strings with no foreign key, so
                  those keep the slug either way. Deactivating only takes it out
                  of the suggestion list. You can restore it from this listing.
                </p>
              </>
            }
            extraActions={
              <LinkButton
                href={`/tags?merge=${row.slug}`}
                variant="ghost"
                shouldReplaceHistory
              >
                Merge
              </LinkButton>
            }
            retireAction={deactivateTagAction.bind(null, row.slug)}
            restoreAction={restoreTagAction.bind(null, row.slug)}
            onCompleted={refresh}
          />
        )}
      />
    </>
  );
};
